// src/components/WelcomeHeading.js
//
// The "Welcome to Mr. Eric's Math class" line.
//
//   <WelcomeHeading variant="dashboard" />
//   <WelcomeHeading variant="class" subject="math" />
//
// All the phrasing lives in utils/displayName.js; all the "whose class is
// this?" logic lives in hooks/useClassEducator.js. This component only
// decides which of the two sentences to ask for and how it looks, so the
// dashboards and the portal can never end up wording the same thing two
// different ways.
//
// It renders nothing while the answer is still resolving. A heading that
// says "Hey Martin!" for half a second and then swaps to "Welcome to Mr.
// Eric's Math class" pulls the eye straight to the flicker; showing the line
// once, correct, is quieter.

import React from "react";
import useAccessLevel from "../hooks/useAccessLevel";
import useClassEducator from "../hooks/useClassEducator";
import { classGreetingParts, dashboardGreeting } from "../utils/displayName";

export default function WelcomeHeading({
  variant = "dashboard",
  subject = "",
  as: Tag = "h2",
  className = "",
  style = {},
  subline = null,
}) {
  const { profile, loading: accessLoading } = useAccessLevel();
  const {
    educator,
    viewerIsEducator,
    loading: educatorLoading,
  } = useClassEducator();

  const waiting = variant === "class" ? educatorLoading : accessLoading;
  if (waiting || !profile) return null;

  // The class line comes back in two pieces so the personal half can carry
  // its own emphasis and, on a narrow screen, wrap onto its own line:
  //
  //     Hey Aiden!
  //     Welcome to Mr. Eric's Science class
  //
  // As one string it would break at whatever word happened to hit the edge,
  // which on a phone is usually mid-name.
  const parts =
    variant === "class"
      ? classGreetingParts({
          viewer: profile,
          educator,
          isEducator: viewerIsEducator,
          subject,
        })
      : {
          hey: "",
          main: dashboardGreeting(profile, { isEducator: viewerIsEducator }),
        };

  // Styling comes from the stylesheets — `.sbl-welcome` in styles/sbl-ui.css
  // for the dashboards, `.portal-welcome` in pages/builder/layoutBuilder.css
  // for subject pages. The inline fallback below is only used when neither
  // class is passed, so this component never ends up as a second, competing
  // definition of what the line looks like.
  const useFallbackStyle = !className;

  return (
    <>
      <Tag
        className={className || undefined}
        style={useFallbackStyle ? { ...s.heading, ...style } : style}
      >
        {parts.hey && (
          <>
            <span className="sbl-welcome-hey">{parts.hey}</span>{" "}
          </>
        )}
        {parts.main}
      </Tag>
      {subline && <p style={s.subline}>{subline}</p>}
    </>
  );
}

const s = {
  // Only reached when no className is supplied. Deliberately plain: if you
  // find yourself editing this to match a page, pass that page's class
  // instead.
  heading: {
    margin: 0,
    fontSize: "clamp(1.15rem, 3.2vw, 1.7rem)",
    lineHeight: 1.25,
    fontWeight: 800,
    color: "#3b0764",
  },
  subline: {
    margin: "6px 0 0",
    fontSize: "clamp(0.82rem, 2vw, 0.95rem)",
    color: "#6b6280",
    lineHeight: 1.5,
  },
};
